import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format, parseISO } from "date-fns";

import { type ShiftPaymentStatus } from "../../api/useGetShiftPaymentStatus";

interface PaymentTimingRowProps {
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  readonly isPaymentStatusLoading: boolean;
}

/**
 * Displays payment timing information based on payment status.
 * - For paid payments: Shows "Paid on [date]"
 * - For pending payments: Shows expected arrival time based on payout method
 * - For failed payments: No timing shown (handled by status pill)
 */
export function PaymentTimingRow({ paymentStatus, isPaymentStatusLoading }: PaymentTimingRowProps) {
  if (isPaymentStatusLoading || !isDefined(paymentStatus)) {
    return null;
  }

  const timing = getPaymentTimingText(paymentStatus);

  if (!isDefined(timing)) {
    return null;
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        {timing.label}
      </Text>
      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.primary })}>
        {timing.value}
      </Text>
    </Stack>
  );
}

interface PaymentTimingText {
  readonly label: string;
  readonly value: string;
}

/**
 * Returns the appropriate timing text based on payment status and method.
 */
function getPaymentTimingText(paymentStatus: ShiftPaymentStatus): PaymentTimingText | undefined {
  const { payoutStatus, payoutMethod, createdAt } = paymentStatus;

  // For paid payments, show the payment date
  if (payoutStatus === "paid" && isDefined(createdAt)) {
    const formattedDate = formatPaymentDate(createdAt);
    const methodLabel = payoutMethod === "instant" ? " via InstantPay" : "";
    return {
      label: "Paid on",
      value: `${formattedDate}${methodLabel}`,
    };
  }

  // For pending payments, show expected arrival based on payout method
  if (payoutStatus === "pending") {
    return {
      label: "Expected",
      value: payoutMethod === "instant" ? "Arrives within 30 minutes" : "2-3 business days",
    };
  }

  // For failed or undefined status, don't show timing
  return undefined;
}

/**
 * Formats an ISO date string to a readable date format.
 * Example: "2026-01-10T15:30:00Z" -> "Jan 10, 2026"
 */
function formatPaymentDate(isoString: string): string {
  try {
    return format(parseISO(isoString), "MMM d, yyyy");
  } catch {
    return isoString;
  }
}
